import React, {useCallback, useContext, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import uniqueId from 'lodash.uniqueid';
import {BasicPage} from 'src/components/basic-page';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {NO_RESULTS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {RemoveAlert} from 'src/modules/platform-alert-builder/all-alerts/tools/remove-alert';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Cell, DataTable, TableHeader} from '@pb/kit-component-data-table-v2';
import {Dropdown} from '@pb/kit-component-dropdown';
import {FormattedDate, Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import {LocaleContext} from '@pb/kit-context-locale';
import {pluralize} from '@pb/kit-formatters';

import {ALERT_TYPE_CAPTIONS} from '../constants';
import {extractTextFromHTML} from '../utils';

import {ActiveAlertsPagination} from './active-alerts-pagination';
import {COLUMNS, COLUMNS_DATA, SORT_FIELDS} from './constants';
import {
    AlertMessageHeader,
    AlertTypeHeader, SortHeader,
} from './headers';
import {useGetActivePlatformAlerts} from './hooks/useGetActivePlatformAlerts';
import {activePlatformAlertsStateActions} from './slices/active-platform-alerts-state.slice';
import {activePlatformAlertsActions} from './slices/active-platform-alerts.slice';
import {ClearFilters} from './tools/clear-filters';

import {activePlatformAlertsAlertPreview} from './active-platform-alerts.module.styl';

import type {
    CellOptions,
    CellProps,
    Column,
    HeaderCellOptions,
    RowsParams,
} from '@pb/kit-component-data-table-v2';
import type {AccessRight} from 'src/hooks/access-rights/constants';

const toCellProps = <DataColumn,>(
    options: CellOptions<DataColumn> | HeaderCellOptions<DataColumn>,
): CellProps<DataColumn> => options as unknown as CellProps<DataColumn>;

export const ActivePlatformAlerts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {locale} = useContext(LocaleContext);
    const {setSuccessMessage} = useContext(MessageContext);

    const {loadingError, result, pagination} = useGetActivePlatformAlerts();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const renderedRows = useMemo(() => (Array.isArray(result?.data) ? result?.data.map(alert => ({
        id: alert.id,
        columnValues: {
            type: ALERT_TYPE_CAPTIONS[alert.alertType],
            message: alert.message,
            startDate: FormattedDate.format({
                value: alert.startDate,
                format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE'),
            }) || '--',
            endDate: FormattedDate.format({
                value: alert.endDate,
                format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE'),
            }) || '--',
            startTime: alert.startTime,
            endTime: alert.endTime,
        },
    })) : undefined), [result?.data, locale.dateFormat]);

    useEffect(() => {
        return () => {
            dispatch(activePlatformAlertsActions.clearData());
            dispatch(activePlatformAlertsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSuccessRemoval = useCallback(() => {
        dispatch(activePlatformAlertsActions.requireRefresh());
        setSuccessMessage('Alert successfully removed');
    }, [dispatch, setSuccessMessage]);

    const navigateToCreateAlertPage = useCallback(() => {
        navigate('/platform-alert-builder/create-alert');
    }, [navigate]);

    const navigateToEditAlertPage = useCallback((alertId: number) => {
        navigate(`/platform-alert-builder/alert/${alertId}`);
    }, [navigate]);

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: renderedRows?.length});
    const totalCount = result?.totalCount || 0;

    return (
        <ServerResponseHandler
            serverError={loadingError}
            accessRight={ACCESS_RIGHTS.PLATFORM_ALERT_BUILDER as AccessRight}
            sectionName="Platform Alert Builder">
            <BasicPage
                title="Platform Alert Builder"
                subtitle={result?.totalCount
                    ? pluralize(`${Integer.format(result.totalCount, locale)} Alert[s]`, result.totalCount)
                    : undefined}
                buttonsBlock={(
                    <Group>
                        <ClearFilters/>
                        <Button variant="Type.PRIMARY" onClick={navigateToCreateAlertPage}>
                            Add Alert
                        </Button>
                    </Group>
                )}>
                <BoxLoading loading={!result}>
                    <DataTable
                        data={{
                            rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                                ...column,
                                width: getColumnWidthByColumnId(column.columnId),
                            })),
                        }}
                        getHeaderTopOffset={() => 88}
                        onStopResize={onStopResize}
                        headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                            switch (column.columnId) {
                                case COLUMNS.ALERT_MESSAGE:
                                    return (
                                        <Cell
                                            {...toCellProps(cellOptions)}
                                            key={`${column.columnId}_${uniqueId()}`}
                                            column={column}
                                            resizable={true}>
                                            <MainColumnHeader
                                                selection={<WidthItem width={50}/>}
                                                maxRowNumber={maxRowNumber}>
                                                <AlertMessageHeader/>
                                            </MainColumnHeader>
                                        </Cell>
                                    );
                                case COLUMNS.ALERT_TYPE:
                                    return (
                                        <Cell
                                            {...toCellProps(cellOptions)}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader>
                                                <AlertTypeHeader/>
                                            </TableHeader>
                                        </Cell>
                                    );
                                default:
                                    return (
                                        <Cell
                                            {...toCellProps(cellOptions)}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader>
                                                <SortHeader
                                                    columnId={column.columnId}
                                                    sortField={SORT_FIELDS[column.columnId]}
                                                />
                                            </TableHeader>
                                        </Cell>
                                    );
                            }
                        }}
                        renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                            const {rowIndex, column: {columnId}} = rowsParams;
                            const value = rowsParams.row.columnValues[columnId] as string | number;
                            const rowId = +rowsParams.row.id;
                            const column = rowsParams.column;
                            const cellKey = uniqueId('cell');
                            const rowNumber = getPaginatedRowNumber({
                                ...pagination,
                                index: rowIndex + 1,
                            });

                            switch (columnId) {
                                case COLUMNS.ALERT_MESSAGE:
                                    const alertMessage = String(value);
                                    return (
                                        <MainColumnCell
                                            key={rowIndex + columnId + alertMessage}
                                            {...{
                                                selection: (
                                                    <div
                                                        className="data-table_hidden-tools data-table_hidden-tools_visible">
                                                        <RemoveAlert alertId={rowId} onSuccess={onSuccessRemoval}/>
                                                    </div>
                                                ),
                                                rowId,
                                                maxRowNumber,
                                                rowNumber,
                                                cellOptions: {...cellOptions, column},
                                            }}>
                                            <a onClick={() => navigateToEditAlertPage(rowId)}>
                                                <Dropdown
                                                    style={{width: '100%'}}
                                                    hoverEnabled={true}
                                                    hoverProps={{
                                                        delay: {
                                                            open: 150,
                                                        },
                                                    }}
                                                    placement="bottom-start"
                                                    anchor={({anchorRef, getAnchorProps}) => (
                                                        <span ref={anchorRef} {...getAnchorProps()}>
                                                            <strong>
                                                                {extractTextFromHTML(alertMessage)}
                                                                <div className="preview-anchor-pointer"/>
                                                            </strong>
                                                        </span>
                                                    )}>
                                                    <Message
                                                        color="warning"
                                                        iconProps={{type: Icons.ERROR, size: Sizes.Size_19}}>
                                                        <div
                                                            className={activePlatformAlertsAlertPreview}
                                                            /* eslint-disable-next-line react/no-danger */
                                                            dangerouslySetInnerHTML={{__html: alertMessage}}
                                                        />
                                                    </Message>

                                                </Dropdown>
                                            </a>
                                        </MainColumnCell>
                                    );
                                default:
                                    const displayValue = value !== -1 ? String(value) : 'N/A';
                                    return (
                                        <Cell
                                            {...toCellProps(cellOptions)}
                                            key={cellKey}
                                            column={column}
                                            className="search-result-cell">
                                            {displayValue}
                                        </Cell>
                                    );
                            }
                        }}
                        paginationStripe={<ActiveAlertsPagination totalCount={totalCount}/>}
                        showNoDataMsg={!!result && !result.data.length}
                        tableMessage={NO_RESULTS_MESSAGE}
                    />
                </BoxLoading>
            </BasicPage>
        </ServerResponseHandler>
    );
};
